// ----- Phase 5.25.2 — ZIP tab content ----- //
//
// Mirrors `XmlTab` (Phase 5.25.1) but for a single-file ZIP upload.
// State machine identical to XmlTab:
//   idle     → single-file picker + dropzone
//   parsing  → indeterminate spinner + filename
//   parsed   → ImportPreviewPanel rendered with the parsed data
//   error    → error block + "Try again" button → idle
//   committing → covered by ImportPreviewPanel's isCommitting prop
//
// Progress feedback: indeterminate spinner only (matches XmlTab). A 100MB
// ZIP can take 10-60s to upload — operators see the spinner during that
// window. Upload-percentage progress would require an XHR-based client
// to read upload progress events; intentionally NOT done here to keep
// the diff tight. Document in the phase report if customer feedback
// warrants progress later.

import { useRef, useState, type ChangeEvent, type DragEvent, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { importApi } from '@/lib/api/import';
import { useUiToast } from '@/hooks/useUiToast';
import { PageToast } from '@/components/shared/PageToast';
import {
  ImportPreviewPanel,
  type ImportParsedData,
} from '@/components/import/ImportPreviewPanel';

type TabState =
  | { kind: 'idle' }
  | { kind: 'parsing'; fileName: string }
  | { kind: 'parsed'; data: ImportParsedData; fileName: string }
  | { kind: 'error'; message: string; detail?: string };

const PARSE_ERROR_MESSAGES: Record<string, string> = {
  NoFilesUploaded: 'No ZIP file was uploaded.',
  InvalidFileType: 'The uploaded file must be a .zip.',
  TooManyFiles: 'Upload exactly one .zip file.',
  FileTooLarge: 'The ZIP is too large (250 MB per file limit).',
  MultipartParseError: 'The upload failed mid-stream. Try again.',
  EmptyZip: 'The ZIP contains no entries.',
  ZipBombDetected: 'The ZIP is too large after extraction.',
  NoBackboneFiles:
    'No eCTD backbone XML found in the ZIP (looked for index.xml, regional XMLs, or *-rps.xml).',
  ParseError: 'The XML inside the ZIP could not be parsed.',
  UnsupportedFormat: 'The ZIP contains XML that is not a recognized eCTD format.',
  MissingRequiredXmlElement: 'The XML inside the ZIP is missing required eCTD elements.',
  AuthRequired: 'Your session expired. Please sign in again.',
  PermissionDenied: 'You do not have permission to import.',
};

const COMMIT_ERROR_MESSAGES: Record<string, string> = {
  MissingRequiredField: 'A required field is missing.',
  EmptySequences: 'No sequences to import.',
  InvalidSequence: 'A sequence is missing required fields.',
  InvalidSummary: 'Import summary is invalid.',
  AuthRequired: 'Your session expired. Please sign in again.',
  PermissionDenied: 'You do not have permission to import.',
};

export function ZipTab(): ReactElement {
  const [state, setState] = useState<TabState>({ kind: 'idle' });
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);
  const fileInputRef = useRef<globalThis.HTMLInputElement | null>(null);
  const { toast, showToast, clearToast } = useUiToast();
  const navigate = useNavigate();

  const triggerPicker = (): void => {
    fileInputRef.current?.click();
  };

  const handleFile = async (file: File): Promise<void> => {
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setState({
        kind: 'error',
        message: 'The uploaded file must be a .zip.',
        detail: `File: ${file.name}`,
      });
      return;
    }
    setState({ kind: 'parsing', fileName: file.name });
    try {
      const data = await importApi.parseZip(file);
      setState({ kind: 'parsed', data, fileName: file.name });
    } catch (err) {
      const message = formatApiError(err, 'Failed to parse ZIP.', PARSE_ERROR_MESSAGES);
      let detail: string | undefined;
      if (err instanceof ApiError) {
        try {
          const body = JSON.parse(err.body) as {
            details?: { detail?: string; fileName?: string };
          };
          if (body.details?.detail) detail = body.details.detail;
          else if (body.details?.fileName) detail = `File: ${body.details.fileName}`;
        } catch {
          /* ignore */
        }
      }
      setState(
        detail !== undefined
          ? { kind: 'error', message, detail }
          : { kind: 'error', message },
      );
    }
  };

  const onPickerChange = (e: ChangeEvent<globalThis.HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  };

  const onDrop = (e: DragEvent<globalThis.HTMLDivElement>): void => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const onDragOver = (e: DragEvent<globalThis.HTMLDivElement>): void => {
    e.preventDefault();
  };

  const handlePreviewChange = (next: ImportParsedData): void => {
    setState((prev) => (prev.kind === 'parsed' ? { ...prev, data: next } : prev));
  };

  const handleCommit = async (data: ImportParsedData): Promise<void> => {
    setCommitting(true);
    setCommitError(null);
    try {
      const result = await importApi.commit(data);
      showToast({
        type: 'success',
        text: `Imported ${result.submissionIds.length} sequence${
          result.submissionIds.length === 1 ? '' : 's'
        } as draft metadata. Document files were not imported.`,
      });
      if (result.submissionIds.length > 0) {
        navigate(`/submissions/${result.submissionIds[0]!}`);
      } else {
        navigate('/home');
      }
    } catch (err) {
      setCommitError(formatApiError(err, 'Failed to import sequences.', COMMIT_ERROR_MESSAGES));
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div data-testid="zip-tab">
      {state.kind === 'idle' && (
        <div
          data-testid="zip-dropzone"
          onDrop={onDrop}
          onDragOver={onDragOver}
          style={{
            background: '#F9FAFB',
            border: '2px dashed #D1D5DB',
            borderRadius: 8,
            padding: '32px 16px',
            textAlign: 'center',
            color: '#6B7280',
            cursor: 'pointer',
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 12 }} aria-hidden="true">
            📦
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Drop an eCTD ZIP here or click to select
          </div>
          <div style={{ fontSize: 12 }}>
            Single .zip file — up to 250 MB. Extracts backbone XMLs in-memory.
          </div>
          <button type="button" onClick={triggerPicker}>Choose ZIP file</button>
          <input
            ref={fileInputRef}
            data-testid="zip-file-picker"
            type="file"
            accept=".zip"
            onChange={onPickerChange}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {state.kind === 'parsing' && (
        <div
          data-testid="zip-parsing"
          style={{
            padding: '24px 16px',
            textAlign: 'center',
            fontSize: 13,
            color: '#6B7280',
          }}
        >
          <div style={{ fontSize: 32, marginBottom: 12 }} aria-hidden="true">
            ⏳
          </div>
          <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Extracting + parsing…
          </div>
          <div style={{ fontSize: 12 }} data-testid="zip-parsing-filename">
            {state.fileName}
          </div>
          <div style={{ marginTop: 8, fontSize: 11, fontStyle: 'italic' }}>
            Large ZIPs (100+ MB) can take 10-60 seconds.
          </div>
        </div>
      )}

      {state.kind === 'parsed' && (
        <>
          {commitError && (
            <div
              data-testid="zip-commit-error"
              role="alert"
              style={{
                marginBottom: 12,
                padding: '8px 12px',
                background: '#FEE2E2',
                color: '#B91C1C',
                border: '1px solid #B91C1C',
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              {commitError}
            </div>
          )}
          <ImportPreviewPanel
            parsedData={state.data}
            isCommitting={committing}
            onChange={handlePreviewChange}
            onCommit={(data) => void handleCommit(data)}
            onCancel={() => {
              setState({ kind: 'idle' });
              setCommitError(null);
            }}
          />
        </>
      )}

      {state.kind === 'error' && (
        <div
          data-testid="zip-parse-error"
          role="alert"
          style={{
            padding: 16,
            background: '#FEE2E2',
            color: '#B91C1C',
            border: '1px solid #B91C1C',
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{state.message}</div>
          {state.detail && (
            <div data-testid="zip-parse-error-detail" style={{ fontSize: 12, opacity: 0.85 }}>
              {state.detail}
            </div>
          )}
          <button
            type="button"
            data-testid="zip-try-again-btn"
            onClick={() => setState({ kind: 'idle' })}
            style={{
              marginTop: 12,
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 600,
              background: '#fff',
              color: '#B91C1C',
              border: '1px solid #B91C1C',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      )}

      <PageToast toast={toast} onDismiss={clearToast} />
    </div>
  );
}
